import { useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import { Plus, Shield, MoreHorizontal, Pencil, Trash2, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  PageHeader,
  DataTable,
  Column,
  ConfirmDialog,
  MetricCard,
  KPIGrid,
} from '@/components/common';
import { rolesApi, Role } from '@/api/services';
import { getErrorMessage } from '@/api/client';

export default function Roles() {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; role?: Role }>({
    open: false,
  });
  const [deleting, setDeleting] = useState(false);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const data = await rolesApi.getAll();
      setRoles(data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  const handleDelete = async () => {
    if (!deleteDialog.role) return;

    try {
      setDeleting(true);
      await rolesApi.delete(deleteDialog.role.id);
      toast.success('Role deleted successfully');
      setDeleteDialog({ open: false });
      fetchRoles();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setDeleting(false);
    }
  };

  const metrics = useMemo(() => {
    const total = roles.length;
    const customRoles = roles.filter((r) => !r.isSystem).length;
    const totalUsers = roles.reduce((sum, r) => sum + (r._count?.userRoles || 0), 0);

    return { total, customRoles, totalUsers };
  }, [roles]);

  const columns: Column<Role>[] = [
    {
      key: 'name',
      header: 'Role',
      sortable: true,
      render: (role) => (
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium">{role.name}</span>
            {role.isSystem && (
              <Badge variant="secondary" className="text-xs">
                System
              </Badge>
            )}
          </div>
          <div className="text-sm text-muted-foreground">{role.code}</div>
        </div>
      ),
    },
    {
      key: 'description',
      header: 'Description',
      render: (role) => <span className="text-muted-foreground">{role.description || '-'}</span>,
    },
    {
      key: 'accessObjects',
      header: 'Permissions',
      render: (role) => (
        <Badge variant="outline">
          {role.accessObjects.length} permission{role.accessObjects.length !== 1 ? 's' : ''}
        </Badge>
      ),
    },
    {
      key: '_count',
      header: 'Users',
      render: (role) => role._count?.userRoles || 0,
    },
    {
      key: 'actions',
      header: '',
      className: 'w-12',
      render: (role) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="More actions" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem disabled={role.isSystem}>
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-destructive"
              disabled={role.isSystem}
              onClick={() => !role.isSystem && setDeleteDialog({ open: true, role })}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          title="Roles & Permissions"
          description="Named permission sets, with the rights granted by each and the accounts holding it."
          actions={
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Create Role
            </Button>
          }
        />
      </motion.div>

      <div>
        <KPIGrid className="mt-6">
          <MetricCard
            label="Total Roles"
            value={metrics.total}
            sentiment="info"
            icon={<Shield className="h-5 w-5" />}
          />
          <MetricCard
            label="Custom Roles"
            value={metrics.customRoles}
            sentiment="success"
            icon={<Pencil className="h-5 w-5" />}
          />
          <MetricCard
            label="Users Assigned"
            value={metrics.totalUsers}
            sentiment="neutral"
            icon={<Users className="h-5 w-5" />}
          />
        </KPIGrid>
      </div>

      <motion.div variants={fadeUp} className="mt-6">
        <DataTable
          data={roles}
          columns={columns}
          loading={loading}
          pagination
          exportable
          columnToggle
          searchable
          emptyTitle="No roles found"
          emptyDescription="Create custom roles to manage user permissions."
          emptyAction={
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Create Role
            </Button>
          }
        />
      </motion.div>

      <ConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ open })}
        title="Delete Role"
        description={`Are you sure you want to delete the "${deleteDialog.role?.name}" role? Users with this role will lose their permissions.`}
        confirmLabel="Delete"
        onConfirm={handleDelete}
        variant="destructive"
        loading={deleting}
      />
    </motion.main>
  );
}
